import React, { Suspense, useState, StrictMode, Fragment, startTransition, useEffect, } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary/dist/react-error-boundary.esm';
import { createFromFetch, createFromReadableStream,
// @ts-ignore
 } from '@shopify/hydrogen/vendor/react-server-dom-vite';
import { RSC_PATHNAME } from './constants.js';
import { ServerPropsProvider } from './foundation/ServerPropsProvider/index.js';
import { ClientAnalytics } from './foundation/Analytics/index.js';
// @ts-expect-error
import CustomErrorPage from 'virtual__error.jsx';
let rscReader;
const cache = new Map();
// Hydrate an SSR response from <meta> tags placed in the DOM.
const flightChunks = [];
const FLIGHT_ATTRIBUTE = 'data-flight';
const requestIdleCallbackHydrogen = (typeof self !== 'undefined' &&
    self.requestIdleCallback &&
    self.requestIdleCallback.bind(window)) ||
    function (cb) {
        const start = Date.now();
        return setTimeout(function () {
            cb({
                didTimeout: false,
                timeRemaining() {
                    return Math.max(0, 50 - (Date.now() - start));
                },
            });
        }, 1);
    };
function addElementToFlightChunks(el) {
    // We don't need to decode, because `.getAttribute` already decodes
    const chunk = el.getAttribute(FLIGHT_ATTRIBUTE);
    if (chunk) {
        flightChunks.push(chunk);
    }
}
// Get initial payload
document
    .querySelectorAll('[' + FLIGHT_ATTRIBUTE + ']')
    .forEach(addElementToFlightChunks);
// Create a mutation observer on the document to detect when new
// <meta data-flight> tags are added, and add them to the array.
const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement &&
                node.tagName === 'META' &&
                node.hasAttribute(FLIGHT_ATTRIBUTE)) {
                addElementToFlightChunks(node);
            }
        });
    });
});
observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
});
if (flightChunks.length > 0) {
    const contentLoaded = new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve));
    try {
        rscReader = new ReadableStream({
            start(controller) {
                const encoder = new TextEncoder();
                const write = (chunk) => {
                    controller.enqueue(encoder.encode(chunk));
                    return 0;
                };
                flightChunks.forEach(write);
                flightChunks.push = write;
                contentLoaded.then(() => {
                    controller.close();
                    observer.disconnect();
                });
            },
        });
    }
    catch (_) {
        // Old browser, will try a new hydration request later
    }
}
const renderHydrogen = async (ClientWrapper) => {
    const root = document.getElementById('root');
    if (!root) {
        console.error(`Could not find a root element <div id="root"></div> to render.`);
        return;
    }
    if (import.meta.hot) {
        import.meta.hot.on('hydrogen-browser-console', ({ type, data }) => {
            if (type === 'warn') {
                console.warn(data);
            }
        });
    }
    let config;
    try {
        config = JSON.parse(root.dataset.clientConfig ?? '{}');
    }
    catch (error) {
        config = {};
        if (__HYDROGEN_DEV__) {
            console.warn('Could not parse client configuration in browser', error.message);
        }
    }
    const RootComponent = 
    // Default to StrictMode on, unless explicitly turned off
    config.strictMode !== false ? StrictMode : Fragment;
    // Fixes hydration in `useId`: https://github.com/Shopify/hydrogen/issues/1589
    const ServerRequestProviderMock = () => null;
    requestIdleCallbackHydrogen(() => {
        startTransition(() => {
            hydrateRoot(root, React.createElement(RootComponent, null,
                React.createElement(ServerRequestProviderMock, null),
                React.createElement(ErrorBoundary, { FallbackComponent: CustomErrorPage
                        ? ({ error }) => (React.createElement(CustomErrorWrapper, { error: error, errorPage: CustomErrorPage }))
                        : DefaultError },
                    React.createElement(Suspense, { fallback: null },
                        React.createElement(Content, { clientWrapper: ClientWrapper })))));
        });
    });
};
export default renderHydrogen;
function Content({ clientWrapper: ClientWrapper = ({ children }) => children, }) {
    const [serverProps, setServerProps] = useState({
        pathname: window.location.pathname,
        search: window.location.search,
    });
    const [rscResponseFromApiRoute, setRscResponseFromApiRoute] = useState(null);
    const response = useServerResponse(serverProps, rscResponseFromApiRoute);
    useEffect(() => {
        // If server props ever change, then use a fresh
        // _rsc request and ignore any response from API routes.
        setRscResponseFromApiRoute(null);
    }, [serverProps]);
    return (React.createElement(ServerPropsProvider, { initialServerProps: serverProps, setServerPropsForRsc: setServerProps, setRscResponseFromApiRoute: setRscResponseFromApiRoute },
        React.createElement(ClientWrapper, null, response.readRoot())));
}
function CustomErrorWrapper({ error, errorPage, }) {
    const Error = React.lazy(errorPage);
    return (React.createElement(ErrorBoundary, { FallbackComponent: ({ error: errorRenderingCustomPage }) => {
            if (import.meta.env.DEV) {
                console.error('Error rendering custom error page:\n' + errorRenderingCustomPage);
            }
            return React.createElement(DefaultError, { error: error });
        } },
        React.createElement(Suspense, { fallback: null },
            React.createElement(Error, { error: error }))));
}
function DefaultError({ error }) {
    return (React.createElement("div", { style: {
            padding: '2em',
            textAlign: 'center',
        } },
        React.createElement("h1", { style: { fontSize: '2em', marginBottom: '1em', fontWeight: 'bold' } }, "Something's wrong here..."),
        React.createElement("div", { style: { fontSize: '1.1em' } },
            React.createElement("p", null, "We found an error while loading this page."),
            React.createElement("p", null,
                "Please, refresh or go back to the",
                ' ',
                React.createElement("a", { href: "/", style: { textDecoration: 'underline' } }, "home page"),
                "."))));
}
function useServerResponse(state, apiRouteRscResponse) {
    const key = JSON.stringify(state);
    if (apiRouteRscResponse) {
        cache.clear();
        cache.set(apiRouteRscResponse.url, apiRouteRscResponse.response);
        return apiRouteRscResponse.response;
    }
    let response = cache.get(key);
    if (response) {
        return response;
    }
    if (rscReader) {
        // The flight response was inlined during SSR, use it directly.
        response = createFromReadableStream(rscReader);
        rscReader = null;
    }
    else {
        if (
        /* @ts-ignore */
        window.BOOMR &&
            /* @ts-ignore */
            window.BOOMR.plugins &&
            /* @ts-ignore */
            window.BOOMR.plugins.Hydrogen) {
            /* @ts-ignore */
            window.BOOMR.plugins.Hydrogen.trackSubPageLoadPerformance();
        }
        ClientAnalytics.resetPageAnalyticsData();
        // Request a new flight response.
        response = createFromFetch(fetch(`${RSC_PATHNAME}?state=` + encodeURIComponent(key)));
    }
    cache.clear();
    cache.set(key, response);
    return response;
}
